import { Box } from '@mui/material';
import React, { createContext, useContext, useRef } from 'react';

type ScrollContextOptions = {
  behavior?: ScrollBehavior;
  top?: number;
  delay?: number;
};

type ScrollContextType = {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  scrollToTop: (options?: ScrollContextOptions) => void;
};

const ScrollContext = createContext<ScrollContextType | null>(null);

export const useScroll = (): ScrollContextType => {
  const context = useContext(ScrollContext);

  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider');
  }

  return context;
};

export const ScrollProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = ({ behavior = 'smooth', top = 0, delay = 0 }: ScrollContextOptions = {}) => {
    if (!scrollRef.current) {
      return;
    }

    setTimeout(() => {
      window.requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top,
          behavior,
        });
      });
    }, delay);
  };

  return (
    <ScrollContext.Provider value={{ scrollRef, scrollToTop }}>
      <Box ref={scrollRef} height="100vh" sx={{ overflowX: 'hidden', overflowY: 'auto' }} flexGrow={1}>
        {children}
      </Box>
    </ScrollContext.Provider>
  );
};
